import React, {useCallback, useState} from 'react';
import {Prism as SyntaxHighlighter} from 'react-syntax-highlighter';
import {oneDark, oneLight} from 'react-syntax-highlighter/dist/esm/styles/prism';
import {useThemeSafe} from '../../contexts/ThemeContext';

export interface CodeBlockRendererProps {
  readonly code: string;
  readonly language?: string;
  readonly inline?: boolean;
  /** 插入代码回调（追加到编辑器） */
  readonly onInsert?: (code: string, language: string) => void;
  /** 是否显示插入按钮 */
  readonly showInsertBtn?: boolean;
}

/**
 * 兼容生产环境的复制到剪贴板方法
 * 优先使用 Clipboard API，失败后 fallback 到 execCommand
 */
const copyToClipboard = async (text: string): Promise<void> => {
  // 方案一：Clipboard API（需要安全上下文）
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // Clipboard API 不可用，继续 fallback
    }
  }

  // 方案二：execCommand fallback（兼容 Electron / HTTP 环境）
  const textarea = document.createElement('textarea');
  textarea.value = text;
  // 移出可视区域，避免闪烁
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
};

export const CodeBlockRenderer: React.FC<CodeBlockRendererProps> = ({
  code,
  language = 'text',
  inline = false,
  onInsert,
  showInsertBtn = false
}) => {
  const {theme} = useThemeSafe();
  const [copied, setCopied] = useState(false);
  const [inserted, setInserted] = useState(false);

  const handleCopy = useCallback(async (): Promise<void> => {
    try {
      await copyToClipboard(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('[CodeBlockRenderer] 复制失败:', err);
    }
  }, [code]);

  const handleInsert = useCallback((): void => {
    onInsert?.(code, language);
    setInserted(true);
    setTimeout(() => setInserted(false), 2000);
  }, [code, language, onInsert]);

  const syntaxStyle = theme === 'dark' ? oneDark : oneLight;

  if (inline) {
    return (
      <code className="inline-code">
        {code}
      </code>
    );
  }

  return (
    <div className="code-block-renderer">
      <div className="code-block-header">
        <span className="code-language">{language}</span>
        <div className="code-block-actions">
          <button
            className="code-copy-btn"
            onClick={handleCopy}
            type="button"
            title="复制代码"
          >
            <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`} />
            <span>{copied ? '已复制' : '复制'}</span>
          </button>
          {showInsertBtn && onInsert && (
            <button
              className="code-copy-btn"
              onClick={handleInsert}
              type="button"
              title="插入到编辑器（追加）"
            >
              <i className={`fas ${inserted ? 'fa-check' : 'fa-file-import'}`} />
              <span>{inserted ? '已插入' : '插入'}</span>
            </button>
          )}
        </div>
      </div>
      <SyntaxHighlighter
        language={language}
        style={syntaxStyle}
        customStyle={{
          margin: 0,
          padding: '16px',
          fontSize: '13px',
          lineHeight: 1.5,
          borderRadius: '0 0 8px 8px'
        }}
        showLineNumbers={code.split('\n').length > 3}
        wrapLongLines
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};